/**
 * sevDesk-Kontakt zu einem Kunden zuordnen, ändern oder lösen.
 *
 * Öffnet mit den Vorschlägen des Servers zu diesem Kunden. Wer tippt, sucht
 * frei in allen sevDesk-Kontakten – Name, Kundennummer, Postleitzahl, Ort.
 * Ein Klick auf einen Treffer verknüpft und schließt das Fenster.
 *
 * „In sevDesk anlegen" gibt es nur, wenn die Anbindung schreiben darf und
 * der Kunde noch keinen Kontakt hat. Es braucht einen zweiten Klick: Das
 * legt einen echten Kontakt in der Buchhaltung an.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfSekundaer } from '../komponenten/Modal';
import { SevdeskTreffer } from '../komponenten/SevdeskTreffer';
import type { Kunde, SevdeskKontakteAntwort, SevdeskVorschlag } from '../typen';

type Antwort = Kunde & { toast?: string };

/** Was das Fenster vom Kunden braucht – Kundenstamm-Zeile und Kunde erfüllen es beide. */
export interface SevdeskKundenBezug {
  id: number;
  name: string;
  kundenNr: string;
  sevdeskId: string;
  sevdeskNummer: string;
  sevdeskUrl: string;
}

export function SevdeskKontaktModal({
  kunde,
  onSchliessen,
}: {
  kunde: SevdeskKundenBezug;
  onSchliessen: () => void;
}) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [eingabe, setEingabe] = useState('');
  const [begriff, setBegriff] = useState('');
  const [fehler, setFehler] = useState('');
  const [anlegenBestaetigen, setAnlegenBestaetigen] = useState(false);

  // 250 ms Ruhe nach dem Tippen, dann suchen – wie in der Kundensuche.
  useEffect(() => {
    const zeitgeber = window.setTimeout(() => setBegriff(eingabe.trim()), 250);
    return () => window.clearTimeout(zeitgeber);
  }, [eingabe]);

  const abfrage = useQuery({
    queryKey: ['sevdesk-kontakte', kunde.id, begriff],
    queryFn: () =>
      api.hole<SevdeskKontakteAntwort>(
        `/kunden/sevdesk/kontakte?kunde=${kunde.id}&q=${encodeURIComponent(begriff)}`,
      ),
  });

  function fertig(a: Antwort, standard: string) {
    void queryClient.invalidateQueries({ queryKey: ['kunden'] });
    void queryClient.invalidateQueries({ queryKey: ['sevdesk-offen'] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
    toastZeigen(a.toast ?? standard);
    onSchliessen();
  }

  function meldung(f: unknown, standard: string) {
    setFehler(f instanceof ApiFehler ? f.message : standard);
  }

  const verknuepfen = useMutation({
    mutationFn: (sevdeskId: string) => api.sende<Antwort>(`/kunden/${kunde.id}/sevdesk`, { sevdeskId }),
    onSuccess: (a) => fertig(a, 'Mit sevDesk verknüpft.'),
    onError: (f) => meldung(f, 'Die Verknüpfung ist fehlgeschlagen.'),
  });

  const loesen = useMutation({
    mutationFn: () => api.loesche<Antwort>(`/kunden/${kunde.id}/sevdesk`),
    onSuccess: (a) => fertig(a, 'Verknüpfung gelöst.'),
    onError: (f) => meldung(f, 'Die Verknüpfung konnte nicht gelöst werden.'),
  });

  const anlegen = useMutation({
    mutationFn: () => api.sende<Antwort>(`/kunden/${kunde.id}/sevdesk/anlegen`),
    onSuccess: (a) => fertig(a, 'In sevDesk angelegt.'),
    onError: (f) => meldung(f, 'Der Kontakt konnte nicht angelegt werden.'),
  });

  const beschaeftigt = verknuepfen.isPending || loesen.isPending || anlegen.isPending;
  const daten = abfrage.data;
  const kontakte: SevdeskVorschlag[] = daten?.kontakte ?? [];
  const verknuepft = !!kunde.sevdeskId;
  const darfAnlegen = !!daten?.aktiv && !verknuepft;

  return (
    <Modal
      titel={verknuepft ? 'sevDesk-Kontakt ändern' : 'sevDesk-Kontakt zuordnen'}
      untertitel={`${kunde.name} · ${kunde.kundenNr}`}
      breite={560}
      fest={beschaeftigt}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={beschaeftigt}>
            Abbrechen
          </KnopfSekundaer>
          {darfAnlegen && (
            <KnopfSekundaer
              onClick={() => {
                if (anlegenBestaetigen) anlegen.mutate();
                else setAnlegenBestaetigen(true);
              }}
              disabled={beschaeftigt}
            >
              {anlegen.isPending
                ? 'Legt an …'
                : anlegenBestaetigen
                  ? 'Ja, in sevDesk anlegen'
                  : 'In sevDesk anlegen'}
            </KnopfSekundaer>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {verknuepft && (
          <div className="sk-stand">
            <span>
              Verknüpft mit Kontakt {kunde.sevdeskNummer || kunde.sevdeskId}
              {kunde.sevdeskUrl && (
                <>
                  {' · '}
                  <a href={kunde.sevdeskUrl} target="_blank" rel="noreferrer">
                    In sevDesk öffnen
                  </a>
                </>
              )}
            </span>
            <button type="button" onClick={() => loesen.mutate()} disabled={beschaeftigt}>
              {loesen.isPending ? 'Löst …' : 'Verknüpfung lösen'}
            </button>
          </div>
        )}

        <label className="modal-feld">
          <span>In sevDesk suchen</span>
          <input
            className="modal-eingabe"
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            placeholder="Name, Kundennummer, PLZ oder Ort …"
            autoFocus
          />
        </label>

        {daten && !daten.lesbar ? (
          <div className="sk-leer">
            sevDesk ist nicht angebunden – ohne Token kann Kora keine Kontakte nachschlagen.
          </div>
        ) : (
          <>
            <div className="sk-kopf">
              <span>{begriff ? 'Treffer in sevDesk' : 'Vorschläge zu diesem Kunden'}</span>
              <span>
                {abfrage.isFetching
                  ? 'Liest sevDesk …'
                  : kontakte.length === 1
                    ? '1 Kontakt'
                    : `${kontakte.length} Kontakte`}
              </span>
            </div>
            <div className="sk-liste">
              {kontakte.map((k) => (
                <SevdeskTreffer
                  key={k.id}
                  kontakt={k}
                  beschaeftigt={beschaeftigt}
                  onWaehlen={(t) => {
                    setFehler('');
                    verknuepfen.mutate(t.id);
                  }}
                />
              ))}
              {!abfrage.isFetching && kontakte.length === 0 && (
                <div className="sk-leer">
                  {abfrage.isError
                    ? 'sevDesk hat nicht geantwortet. Bitte gleich noch einmal versuchen.'
                    : begriff
                      ? 'Nichts gefunden. Anders schreiben oder nur den Nachnamen probieren.'
                      : 'Kein passender Kontakt gefunden – oben suchen, oder den Kunden in sevDesk anlegen.'}
                </div>
              )}
            </div>
          </>
        )}

        {anlegenBestaetigen && !anlegen.isPending && (
          <div className="modal-hinweis">
            Legt „{kunde.name}" als neuen Kontakt in sevDesk an – mit Adresse und Kontaktwegen aus
            Kora. Nochmals klicken bestätigt.
          </div>
        )}
        {fehler && <div className="modal-fehler">{fehler}</div>}
      </div>
    </Modal>
  );
}
